import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminStore } from '../../../store/adminStore';
import { games as defaultGames } from '../../../mocks/games';
import { communityPosts } from '../../../mocks/community';
import AdminSidebar from '../components/AdminSidebar';
import HomeEditor from '../components/HomeEditor';
import GamesEditor from '../components/GamesEditor';
import CommunityEditor from '../components/CommunityEditor';
import KnowledgeEditor from '../components/KnowledgeEditor';
import TeacherEditor from '../components/TeacherEditor';
import { useAuth } from '@/contexts/AuthContext';
import type { AdminSection } from '../types';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading, signOut, user } = useAuth();
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [storeData, setStoreData] = useState(() => adminStore.getStore());

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin');
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    const handler = () => setStoreData(adminStore.getStore());
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/admin');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-lg font-semibold">Đang kiểm tra quyền truy cập...</div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const totalGames = defaultGames.length + storeData.customGames.length;
  const visibleGames = totalGames - storeData.hiddenGameIds.filter(
    id => [...defaultGames, ...storeData.customGames].some(g => g.id === id)
  ).length;

  const stats = [
    {
      label: 'Trò chơi đang hiển thị',
      value: visibleGames,
      total: totalGames,
      icon: 'ri-gamepad-line',
      color: 'text-orange-500',
      bg: 'bg-orange-50',
      onClick: () => setActiveSection('games'),
    },
    {
      label: 'Bài đăng cộng đồng',
      value: communityPosts.length - storeData.hiddenPostIds.filter(id => communityPosts.some(p => p.id === id)).length,
      total: communityPosts.length,
      icon: 'ri-community-line',
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      onClick: () => setActiveSection('community'),
    },
    {
      label: 'KHBD hiện có',
      value: storeData.lessonPlans.length,
      total: null,
      icon: 'ri-file-text-line',
      color: 'text-green-600',
      bg: 'bg-green-50',
      onClick: () => setActiveSection('teacher'),
    },
    {
      label: 'Bài đang bị ẩn',
      value: storeData.hiddenPostIds.filter(id => communityPosts.some(p => p.id === id)).length,
      total: null,
      icon: 'ri-eye-off-line',
      color: 'text-red-400',
      bg: 'bg-red-50',
      onClick: () => setActiveSection('community'),
    },
  ];

  const sectionTitles: Record<AdminSection, string> = {
    overview: 'Tổng quan',
    home: 'Chỉnh sửa Trang chủ',
    games: 'Quản lý Trò chơi',
    community: 'Quản lý Cộng đồng',
    teacher: 'Quản lý KHBD',
    knowledge: 'Quản lý Video – Hạt giống tri thức',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar
        active={activeSection}
        onSelect={setActiveSection}
        onLogout={handleLogout}
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main area */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-all duration-300"
        style={{ marginLeft: sidebarOpen ? 256 : 64 }}
      >
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-bold text-gray-800">{sectionTitles[activeSection]}</h1>
            <p className="text-xs text-gray-400">Nhà phát hành · {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-sm"></i>
              </div>
              Xem website
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-sm text-red-500 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-sm"></i>
              </div>
              Đăng xuất
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="p-6 flex-1">
          {activeSection === 'overview' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-extrabold text-gray-800 mb-1">
                  Xin chào, Nhà phát hành!
                </h2>
                <p className="text-gray-500">Quản lý toàn bộ nội dung website từ đây.</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {stats.map((s) => (
                  <button
                    key={s.label}
                    onClick={s.onClick}
                    className="bg-white rounded-2xl p-5 text-left hover:shadow-sm transition-all cursor-pointer group"
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${s.bg}`}>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${s.icon} text-lg ${s.color}`}></i>
                      </div>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-800">
                      {s.value}
                      {s.total !== null && (
                        <span className="text-base font-normal text-gray-400">/{s.total}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-gray-800 mb-4">Truy cập nhanh</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Sửa Trang chủ', icon: 'ri-home-4-line', section: 'home' as AdminSection, desc: 'Tiêu đề, mô tả', color: 'hover:border-orange-300' },
                    { label: 'Quản lý Game', icon: 'ri-gamepad-line', section: 'games' as AdminSection, desc: 'Thêm, ẩn trò chơi', color: 'hover:border-yellow-300' },
                    { label: 'Cộng đồng', icon: 'ri-community-line', section: 'community' as AdminSection, desc: 'Duyệt bài đăng', color: 'hover:border-green-300' },
                    { label: 'Giáo viên', icon: 'ri-graduation-cap-line', section: 'teacher' as AdminSection, desc: 'Quản lý KHBD', color: 'hover:border-red-200' },
                    { label: 'Hạt giống TT', icon: 'ri-seedling-line', section: 'knowledge' as AdminSection, desc: 'Quản lý video', color: 'hover:border-orange-200' },
                  ].map((qa) => (
                    <button
                      key={qa.label}
                      onClick={() => setActiveSection(qa.section)}
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 border-gray-100 transition-all cursor-pointer ${qa.color} hover:bg-gray-50`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl">
                        <i className={`${qa.icon} text-lg text-gray-500`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-700 whitespace-nowrap">{qa.label}</div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">{qa.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 bg-orange-50 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
                  <i className="ri-information-line text-orange-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-orange-800 text-sm mb-1">Lưu ý quan trọng</h4>
                  <p className="text-xs text-orange-600 leading-relaxed">
                    Tất cả thay đổi được lưu trực tiếp vào trình duyệt và hiển thị ngay lập tức trên website.
                    Mỗi lần chỉnh sửa, nhấn <strong>"Lưu thay đổi"</strong> để áp dụng.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'home' && <HomeEditor />}
          {activeSection === 'games' && <GamesEditor />}
          {activeSection === 'community' && <CommunityEditor />}
          {activeSection === 'teacher' && <TeacherEditor />}
          {activeSection === 'knowledge' && <KnowledgeEditor />}
        </main>
      </div>
    </div>
  );
}
